/**
 * Where the shopper wants delivery, and what Flow's delivery engine promises there.
 *
 * - The address book is the one the live theme's "Deliver to" box already keeps in this
 *   browser (localStorage 'chakki.deliverTo.v1' = {saved: [{id, label, tag, address, lat, lng,
 *   km, ...}], chosenId}), so a shopper who set an address on the old site keeps it here. The
 *   address sheet (build step 11) writes the same record.
 * - The promise comes from Flow: POST /shopify_integration/delivery/promise/ with the spot and
 *   the basket value. Its words ("Today before 6pm") are the words checkout's rate uses, so the
 *   header and checkout never disagree.
 * - Checkout learns the spot from a hidden line property, `_chakki_loc` = "lat,lng", on every
 *   basket line: Shopify sends line properties to the carrier-rate callback, never cart
 *   attributes. New lines get it from cw-cart.js; older lines are stamped once per page here,
 *   as the old theme did (its comment, kept: "This is the mechanism, not a nicety").
 * - Beside it, `_chakki_for` = the address that pin belongs to, exactly as checkout gets it
 *   (line 1, line 2, pincode). Flow prices from the pin only while checkout's address is that
 *   one: a shopper who picks another address in checkout is priced for where it really goes
 *   (owner, 1 Oct; Flow shopify_integration/carrier.py). The promise call sends the same lines
 *   so Flow remembers each measured address by them.
 *
 * - The address sheet (assets/cw-address.js) reads and writes the book through book() /
 *   saveBook(), measures a pin with quote(), and gets the map key and the delivery edge from
 *   settings() (Flow's config answer).
 * - CHECKOUT carries the chosen address into Shopify's checkout as URL fields, as the live
 *   theme does (its commit "Carry the chosen address into checkout"): the shopper types less,
 *   and the address checkout sends to the delivery rate is the one that was measured.
 *
 * Document events: 'cw:delivery:change' (detail = view()) whenever the words change;
 * 'cw:address:change' (detail = {book}) whenever the book is saved.
 */
import { cart } from '@cw/cart';

const STORE_KEY = 'chakki.deliverTo.v1';
const VIEW_KEY = 'cw:delivery:view';
const ANSWER_KEY = 'cw:delivery:answer';
const ORIGIN_KEY = 'cw:delivery:origin';
const SLOT_KEY = 'cw:delivery:slot';
export const LOC_PROPERTY = '_chakki_loc';
export const FOR_PROPERTY = '_chakki_for';
/** Whose account this browser's addresses were synced with (cw-book-sync.js). */
export const OWNER_KEY = 'cw:book:owner';

/*
 * Signed out after signing in here (LOG OUT, or Shopify's own account page), or another
 * shopper signed in: the addresses were theirs, and they live on in their account. A shared
 * family phone forgets them (owner, 2 Oct). A guest's own addresses (no owner) are kept, and go
 * up to the first account that signs in.
 */
try {
  const owner = localStorage.getItem(OWNER_KEY);
  const who = document.body?.dataset.customer || '';
  if (owner && owner !== who) {
    localStorage.removeItem(STORE_KEY);
    localStorage.removeItem(OWNER_KEY);
    for (const key of ['cw:delivery:view', 'cw:delivery:answer', 'cw:book:synced']) sessionStorage.removeItem(key);
  }
} catch {
  // storage off: nothing was kept, so nothing to forget
}

const flowBase = () =>
  (document.querySelector('meta[name="cw-flow"]')?.getAttribute('content') || 'https://flow.chakkiwale.com').replace(/\/+$/, '');

function load(key, fallback, store = sessionStorage) {
  try {
    return JSON.parse(store.getItem(key) || 'null') ?? fallback;
  } catch {
    return fallback;
  }
}
function save(key, value, store = sessionStorage) {
  try {
    store.setItem(key, JSON.stringify(value));
  } catch {
    // private window or full storage: the page still works, it just forgets
  }
}

/** The address book: {saved: [...], chosenId, pinnedId}. */
export function book() {
  const b = load(STORE_KEY, null, localStorage) || {};
  return { saved: Array.isArray(b.saved) ? b.saved : [], chosenId: b.chosenId || null, pinnedId: b.pinnedId ?? null };
}

/** The address the shopper chose, or null. */
export function chosen() {
  const b = book();
  return b.saved.find((a) => a.id === b.chosenId) || null;
}

/**
 * Write the book without moving anything: the account's ids on records (cw-book-sync.js).
 * Lists repaint ('cw:address:change'); the lines and the promise stay as they are.
 */
export function keepBook(next) {
  save(STORE_KEY, { saved: next.saved || [], chosenId: next.chosenId || null, pinnedId: next.pinnedId ?? null }, localStorage);
  document.dispatchEvent(new CustomEvent('cw:address:change', { detail: { book: book(), kept: true } }));
}

/** The old theme's stand-in name for a pin with no name; never a building. */
const NO_NAME = 'That address';

/**
 * An address a rider can find: a flat, a building, a pincode and a spot. Addresses saved on the
 * old theme can lack all but the spot (its "That address"); those are completed before checkout.
 */
export function isComplete(a) {
  // From the account, as checkout showed it (cw-book-sync.js): its line 1, pincode and a spot.
  if (a?.lines?.address1) return !!(a.pincode && a.lat != null && a.lng != null);
  const building = a?.apartment || (a?.label && a.label !== NO_NAME ? a.label : '');
  return !!(a && String(a.flat || '').trim() && String(building).trim() && a.pincode && a.lat != null && a.lng != null);
}

/** The last restamp of the basket lines, so checkout can wait for it. */
let settling = Promise.resolve();

/**
 * Write the book. When the chosen address (or its spot) changed, the page's words and the
 * basket lines follow at once, and Flow is asked again. So do the lines when only the words
 * changed (a flat added, a building renamed): `_chakki_for` must name the address checkout gets.
 */
export function saveBook(next) {
  const before = chosen();
  save(STORE_KEY, { saved: next.saved || [], chosenId: next.chosenId || null, pinnedId: next.pinnedId ?? null }, localStorage);
  const after = chosen();
  const moved = !before || !after || before.id !== after.id || before.lat !== after.lat || before.lng !== after.lng;
  const renamed = !moved && placeFor(before) !== placeFor(after);
  if (moved || renamed) {
    announce();
    settling = refresh().then(stampLines);
  }
  document.dispatchEvent(new CustomEvent('cw:address:change', { detail: { book: book() } }));
}

/** "lat,lng" for the line property, or ''. */
export function spot() {
  const here = chosen();
  if (!here || here.lat == null || here.lng == null) return '';
  return `${Number(here.lat).toFixed(6)},${Number(here.lng).toFixed(6)}`;
}

/**
 * The address as checkout's two lines, Shopify's own split (owner, 1 Oct):
 * - line 1 = the building and where it is ("Prestige Sunrise Park, Neotown Road, Electronic
 *   City"): what Google's suggestions return, so a shopper who re-picks it in checkout gets the
 *   society name back, and a lookup of it lands on the building;
 * - line 2 = "Apartment, suite" ("1163, Tower 1, Floor 16, Near gate 2"): a Google pick in
 *   checkout leaves it alone, so the flat survives.
 * The apartment name is in both the order Flow reads and the address Shopify keeps.
 */
export function checkoutLines(here) {
  // An address from the shopper's account that nobody has edited here: Shopify's own lines,
  // word for word, so `_chakki_for` and checkout agree (cw-book-sync.js).
  if (here.lines && (here.lines.address1 || here.lines.address2)) {
    return { address1: here.lines.address1 || '', address2: here.lines.address2 || '', city: here.city || 'Bengaluru', zip: String(here.pincode || '').trim() };
  }
  const seen = [];
  const add = (part) => {
    const text = String(part || '').trim();
    const low = text.toLowerCase();
    if (text && !seen.some((s) => s.toLowerCase().includes(low))) seen.push(text);
  };
  const building = String(here.apartment || here.building || (here.label !== NO_NAME ? here.label : '') || '').trim();
  add(building);
  add(here.street);
  add(here.area);
  const line1 = seen.join(', ') || String(here.address || '').split(',').slice(0, 3).join(',').trim();
  // The building again in line 2 ("Apartment, suite"): a Google pick in checkout replaces line 1,
  // and the society name must survive it (owner, 2 Oct).
  const line2 = [here.flat, here.tower, here.floor ? `Floor ${here.floor}` : '', here.apartment || here.building, here.landmark]
    .map((p) => String(p || '').trim())
    .filter(Boolean)
    .filter((p, i, all) => all.findIndex((q) => q.toLowerCase() === p.toLowerCase()) === i)
    .join(', ');
  return { address1: line1, address2: line2, city: here.city || 'Bengaluru', zip: String(here.pincode || '').trim() };
}

/** What `_chakki_for` says: the three lines Flow compares with checkout's address. */
function placeFor(here) {
  const l = checkoutLines(here);
  return l.address1 || l.address2 ? [l.address1, l.address2, l.zip].join('\n') : '';
}

/** The pill's word: the address's tag ("Home") or the first part of its label, kept short. */
function shortLabel(here) {
  const name = String(here.tag || here.label || here.address || '').split(',')[0].trim();
  return name.length > 16 ? `${name.slice(0, 15)}…` : name;
}

/** The address's tag as the address sheet shows it: "Home", "Work", "Family", or its own; '' for none. */
const tagOf = (here) => String(here.tag || { home: 'Home', work: 'Work', family: 'Family' }[here.kind] || '').trim();

const lowerFirst = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : '');

/** The kind of delivery the shopper picked in the slot sheet: 'standard' (default) or 'express'. */
export function slotChoice() {
  return load(SLOT_KEY, 'standard');
}

/**
 * Pick a kind of delivery (the slot sheet). The header follows at once; the words go to the
 * basket as the `delivery_slot` attribute, and checkout's own rate list is where it is
 * confirmed (slot-picker.md "Rules").
 */
export function chooseSlot(kind, words) {
  save(SLOT_KEY, kind);
  announce();
  fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes: { delivery_slot: words || kind } }),
  }).catch(() => {});
}

let answer = load(ANSWER_KEY, null);

/**
 * The slot line before there is a promise to show. The design's screens always have an
 * address; its prototype keeps "Check when we can deliver to you" for this state, which the
 * phone row cuts to "Check when we ca…" beside "Set location", so the line says it in three
 * words (design-handoff/open-questions.md).
 */
const ASK = 'See delivery times';

/**
 * What the header says. No address: the design's own words for that state.
 * Not serviceable: pick-up (the design's far tier). Otherwise the engine's standard promise.
 */
export function view() {
  const here = chosen();
  if (!here) {
    // No address yet. The standard promise depends on the time of day, not the distance, so
    // the header can still say when (as the design's first-visit screen does); the pill asks
    // for the address. Before Flow answers: the design's own no-location words.
    const g = answer && answer.spot === 'origin' && answer.serviceable ? answer.standard || {} : null;
    if (!g || !g.when) return { label: 'Set location', line1: ASK, line2: '', tier: 'near', known: false };
    const line2 = g.order_by ? `· Order by ${g.order_by}${g.order_by_tomorrow ? ' tomorrow' : ''}` : '';
    return { label: 'Set location', line1: g.when, line2, tier: /^tomorrow/i.test(g.when) ? 'mid' : 'near', known: false, answer };
  }
  const label = shortLabel(here);
  const a = answer && answer.spot === spot() ? answer : null;
  if (!a) {
    const last = load(VIEW_KEY, null);
    return last && last.label === label ? last : { label, line1: ASK, line2: '', tier: 'near', known: false };
  }
  if (!a.serviceable) {
    // Beyond the delivery edge: pick up only (v13 1j), ready when Flow says the bench can have it.
    const ready = a.pickup?.when ? `· Ready ${lowerFirst(a.pickup.when)}` : '· Electronic City';
    return { label, line1: 'Pick up from our shop', line2: ready, tier: 'far', known: true, answer: a };
  }
  if (slotChoice() === 'express' && a.express) {
    return { label, line1: 'Express', line2: `· Arrives ${lowerFirst(a.express.when)}`, tier: 'near', known: true, answer: a };
  }
  const s = a.standard || {};
  const line2 = s.order_by ? `· Order by ${s.order_by}${s.order_by_tomorrow ? ' tomorrow' : ''}` : '';
  return { label, line1: s.when || '', line2, tier: /^tomorrow/i.test(s.when || '') ? 'mid' : 'near', known: true, answer: a };
}

function announce() {
  const v = view();
  save(VIEW_KEY, { label: v.label, line1: v.line1, line2: v.line2, tier: v.tier, known: v.known });
  document.dispatchEvent(new CustomEvent('cw:delivery:change', { detail: v }));
}

let inflight = null;
/** Ask Flow again for the chosen spot and this basket. */
/**
 * Flow's settings answer. Besides the map key it carries today's standard promise
 * (`standard`: when / order_by), which depends on the clock and not on the distance, so a
 * first-time visitor's header can say it. Kept five minutes: the windows move by the hour.
 */
async function config() {
  const known = load(ORIGIN_KEY, null);
  if (known && known.config && 'maps_key' in known.config && Date.now() - known.at < 5 * 60 * 1000) return known.config;
  const response = await fetch(`${flowBase()}/shopify_integration/delivery/config/`);
  if (!response.ok) throw new Error(`config ${response.status}`);
  const fresh = await response.json();
  const keep = {
    standard: fresh.standard || null,
    origin: fresh.origin,
    max_km: fresh.max_km,
    maps_key: fresh.maps_key || '',
    bias_radius_m: fresh.bias_radius_m,
    country: fresh.country || 'in',
  };
  save(ORIGIN_KEY, { at: Date.now(), config: keep });
  return keep;
}

/** Flow's settings (the map key, the mill, the delivery edge, today's standard promise). */
export const settings = config;

/** The basket's value in rupees, as Flow's free-delivery bar reads it. */
function basketValue() {
  return Math.round(cart.items.reduce((sum, i) => sum + (i.final_line_price ?? i.line_price ?? 0), 0) / 100);
}

/**
 * Flow's answer for any spot (the map's pin), without making it the chosen one.
 * Resolves {ok, status, body}; 429 = too many new spots for now, 503 = not measured.
 */
export async function quote(where, signal) {
  const response = await fetch(`${flowBase()}/shopify_integration/delivery/promise/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ lat: where.lat, lng: where.lng, address: where.address || '', value: basketValue() }),
    signal,
  });
  const body = await response.json().catch(() => ({}));
  return { ok: response.ok, status: response.status, body };
}

export async function refresh() {
  const here = chosen();
  const value = basketValue();
  if (inflight) inflight.abort();
  inflight = new AbortController();
  try {
    if (!here || !spot()) {
      // No address: only the time-of-day promise (see view()).
      const c = await config();
      answer = c.standard ? { serviceable: true, standard: c.standard, spot: 'origin', value } : null;
      save(ANSWER_KEY, answer);
      announce();
      return answer;
    }
    const lines = checkoutLines(here);
    const where = { lat: here.lat, lng: here.lng, address: here.address || '', address1: lines.address1, address2: lines.address2, zip: lines.zip };
    const key = spot();
    const response = await fetch(`${flowBase()}/shopify_integration/delivery/promise/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...where, value }),
      signal: inflight.signal,
    });
    if (!response.ok) throw new Error(`promise ${response.status}`);
    answer = Object.assign(await response.json(), { spot: key, value });
    save(ANSWER_KEY, answer);
  } catch (error) {
    if (error.name === 'AbortError') return null;
    // Keep the last answer on a hiccup; the words were true a moment ago.
  }
  announce();
  return answer;
}

/** New lines carry the spot (cw-cart.js asks for these properties on every add). */
cart.lineProperties = () => {
  const value = spot();
  if (!value) return {};
  const here = chosen();
  const place = here ? placeFor(here) : '';
  return place ? { [LOC_PROPERTY]: value, [FOR_PROPERTY]: place } : { [LOC_PROPERTY]: value };
};

/** Lines added before the address was set, or on the old theme: stamp them once. */
async function stampLines() {
  const value = spot();
  if (!value) return;
  const want = cart.lineProperties();
  const stale = cart.items.filter(
    (item) => !item.properties || Object.entries(want).some(([key, v]) => item.properties[key] !== v)
  );
  if (!stale.length) return;
  for (const item of stale) {
    const properties = Object.assign({}, item.properties || {}, want);
    if (!want[FOR_PROPERTY]) delete properties[FOR_PROPERTY];
    await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: item.key, properties }),
    }).catch(() => {});
  }
  await cart.refresh().catch(() => {});
}

// The basket's value moves the free-delivery line: ask again when it settles.
let timer = null;
document.addEventListener('cw:cart:change', () => {
  clearTimeout(timer);
  timer = setTimeout(refresh, 700);
});
// Another tab (or the address sheet) changed the chosen address.
window.addEventListener('storage', (event) => {
  if (event.key === STORE_KEY) refresh();
});

/*
 * CHECKOUT carries the chosen address (the live theme's rule, kept): Shopify's checkout takes
 * a shipping address in the URL, verified on this shop as a guest. Anything unexpected and
 * the click is left as it was; a shopper must never be kept from checkout by this. Nothing
 * happens until an address with a pincode is chosen.
 */
const CHECKOUT_HIT = 'button[name="checkout"], input[name="checkout"], a[href*="/checkout"], [data-checkout]';

function checkoutUrl(here) {
  // Myself: the signed-in shopper's own name and number (the address form no longer asks).
  const body = document.body.dataset;
  const mine = here.me !== false;
  const name = String(here.name || (mine ? body.customerName : '') || '').trim();
  const phone = here.phone || (mine ? body.customerPhone : '') || '';
  const space = name.indexOf(' ');
  // The same two lines `_chakki_for` names, so Flow knows this pin is this address.
  const lines = checkoutLines(here);
  const fields = {
    'checkout[shipping_address][first_name]': space > 0 ? name.slice(0, space) : name,
    'checkout[shipping_address][last_name]': space > 0 ? name.slice(space + 1) : '',
    'checkout[shipping_address][address1]': lines.address1,
    'checkout[shipping_address][address2]': lines.address2,
    // The mill delivers 35 km, so city and state are not in question; the place's own city
    // wins when Google gave one.
    'checkout[shipping_address][city]': lines.city,
    'checkout[shipping_address][province]': 'Karnataka',
    'checkout[shipping_address][country]': 'India',
    'checkout[shipping_address][zip]': lines.zip,
    'checkout[shipping_address][phone]': phone,
  };
  const parts = Object.entries(fields)
    .filter(([, value]) => value)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
  return parts.length ? `${window.Shopify?.routes?.root || '/'}checkout?${parts.join('&')}` : '';
}

/**
 * An incomplete address is completed before checkout (its form opens with one line saying
 * why), and checkout carries on by itself once it is saved. Ten minutes, then the shopper's
 * next CHECKOUT asks again rather than a stale wish taking them away mid-browse.
 */
let resumeAt = 0;
document.addEventListener('cw:address:change', async (event) => {
  if (event.detail?.kept || !resumeAt || Date.now() - resumeAt > 10 * 60 * 1000) return;
  const here = chosen();
  if (!isComplete(here)) return;
  resumeAt = 0;
  await settling.catch(() => {});
  goToCheckout(here);
});

function goToCheckout(here) {
  const url = checkoutUrl(here);
  if (!url) return false;
  // The address's name ("Home") goes with the basket as `address_label`, for the order
  // pages' "Delivering to" card (chakkiwale-order-page shared/address.js). Flow ignores it.
  let labelled = false;
  fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    // `address_label_for`: the address the name belongs to, so the order pages show "Home"
    // only when checkout ended on that address (chakkiwale-order-page shared/address.js).
    body: JSON.stringify({ attributes: { address_label: tagOf(here), address_label_for: placeFor(here) } }),
  })
    .catch(() => {})
    .finally(() => {
      labelled = true;
    });
  // A quantity still on its way to Shopify lands first (at most 4 s), then checkout.
  const started = Date.now();
  const go = () =>
    (!cart.busy && labelled) || Date.now() - started > 4000 ? window.location.assign(url) : setTimeout(go, 100);
  go();
  return true;
}

/**
 * Signing in is asked at CHECKOUT, not before (owner, 2 Oct; how quick-commerce shops work):
 * browsing, the basket and delivery times stay open to everyone. After the code, checkout goes
 * on by itself (cw-signin.js says 'cw:checkout:go'). If signing in fails, the sign-in sheet
 * offers "Continue without signing in", which marks this visit as a guest's (GUEST_KEY).
 */
export const GUEST_KEY = 'cw:checkout:guest';
const guestChose = () => {
  try {
    const at = Number(sessionStorage.getItem(GUEST_KEY) || 0);
    return at && Date.now() - at < 30 * 60 * 1000;
  } catch {
    return false;
  }
};
// The basket's button says LOG IN TO CHECKOUT logged out; a guest by choice sees CHECKOUT.
document.documentElement.classList.toggle('cw-as-guest', !document.body.dataset.customer && !!guestChose());

/**
 * Everything CHECKOUT asks before Shopify's checkout opens: signed in (or a guest by choice),
 * then an address a rider can find. Returns false when it took over (sign-in or the form
 * opened), true when it went on to checkout, null when there is nothing for it to do.
 */
function startCheckout() {
  if (!document.body.dataset.customer && !guestChose()) {
    document.dispatchEvent(new CustomEvent('cw:signin:open', { detail: { then: 'checkout' } }));
    return false;
  }
  const here = chosen();
  if (!here) return null;
  if (!isComplete(here)) {
    resumeAt = Date.now();
    document.dispatchEvent(new CustomEvent('cw:address:open', { detail: { editId: here.id, ask: true } }));
    return false;
  }
  return goToCheckout(here) ? true : null;
}

// `detail.opening` tells the asker whether checkout is opening now (false: a form came first).
document.addEventListener('cw:checkout:go', (event) => {
  const went = startCheckout();
  if (went === null) window.location.assign(`${window.Shopify?.routes?.root || '/'}checkout`);
  if (event.detail) event.detail.opening = went !== false;
});

document.addEventListener(
  'click',
  (event) => {
    try {
      if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey) return;
      const hit = event.target?.closest?.(CHECKOUT_HIT);
      if (!hit) return;
      const signedOut = !document.body.dataset.customer && !guestChose();
      const here = chosen();
      // Nothing for us to add (signed in, no address chosen): Shopify's checkout as it is.
      if (!signedOut && (!here || (isComplete(here) && !checkoutUrl(here)))) return;
      event.preventDefault();
      startCheckout();
    } catch {
      // leave the click alone
    }
  },
  true
);

announce();
refresh().then(stampLines);
